import Service from '@ember/service';
import Evented from '@ember/object/evented';
import ExtensionLogger from '../../ExtensionLogger.ts';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { type EventData, Extension, type AppEvent } from '../../Extension.ts';

export const ExtensionEventNames = {
  EXTENSION_UPDATE: 'extensionUpdate'
};

export interface AppEventHandler {
  extensionListening: Extension;
  eventToListen: string;
  handler: (event: AppEvent) => void;
  registeredHandler?: (event: AppEvent) => void;
  isOn: boolean;
}

/**
 * example event subscription for Ember:
 *
 * 1- Inject service on ember side:
 * @service('extensions/extension-events') extensionEvents
 *
 * 2- Subscribe to event on ember side
 * this.extensionEvents.subscribeToExtensionEvent('SHOULD-MATCH', (message: unknown, data: unknown) => {
 *   console.log(this, message, data)
 * })
 *
 * 3- Send event from extension
 * this.dispatchEventToPeek({
 *         eventName: 'SHOULD-MATCH', // this event name should match on the ember side
 *         data: { message: 'This event comes from extension code!' }
 *       })
 */
export default class ExtensionEventsService extends Service.extend(Evented) {
  @tracked subscribedExtensionEvents = new Map();
  @tracked subscribedAppEvents: Map<string, AppEventHandler[]> = new Map();

  /**
   * send an event to all subscribers, this is being called from inside Extensions classes
   * we pass this function ref to Extension class instances when we set them up
   * @param message
   */
  @action
  sendEvent(message: EventData = {}): void {
    ExtensionLogger.log(
      'Ember - Service::ExtensionEvents::sendEvent(): received message',
      message
    );
    const eventName = message.eventName ?? ExtensionEventNames.EXTENSION_UPDATE;

    // @ts-expect-error trigger is defined in Evented
    this.trigger(eventName, message);
  }

  /**
   * subscribe to extension events from any Ember construct
   * @param eventName
   * @param handler
   */
  @action
  subscribeToExtensionEvent(
    eventName: string = ExtensionEventNames.EXTENSION_UPDATE,
    handler: (event: Event) => void
  ): void {
    // only subscribe if event is not already subscribed with the same handler
    if (!this.subscribedExtensionEvents.has(eventName)) {
      this.subscribedExtensionEvents.set(eventName, handler);

      // @ts-expect-error on is defined in Evented
      this.on(eventName, (event: Event) => {
        handler(event);
      });
    }
  }

  /**
   * used to subscribe to app events from extensions code
   * @param extensionListening
   * @param eventName
   * @param handler
   */
  @action
  subscribeToAppEvent(
    extensionListening: Extension,
    eventName: string,
    handler: (event: AppEvent) => void
  ): void {
    // check if extensionListening is already subscribed to this event
    const existingHandlers = this.subscribedAppEvents.get(eventName) ?? [];
    const existingHandler = existingHandlers.find(
      (h) => h.extensionListening === extensionListening
    );

    if (existingHandler) {
      console.log(
        `Extension already subscribed to ${eventName}, skipping duplicate subscription`
      );
      return;
    }

    const handlerObject: AppEventHandler = {
      extensionListening,
      eventToListen: eventName,
      handler,
      registeredHandler: undefined,
      isOn: false
    };

    handlerObject.registeredHandler = (event: AppEvent) => {
      handlerObject.handler(event);
    };

    if (!this.subscribedAppEvents.has(eventName)) {
      this.subscribedAppEvents.set(eventName, [handlerObject]);
    } else {
      this.subscribedAppEvents.set(eventName, [
        ...existingHandlers,
        handlerObject
      ]);
    }

    // @ts-expect-error off is defined in Evented
    this.on(eventName, handlerObject.registeredHandler);
    handlerObject.isOn = true;
  }

  @action
  cleanupAppEventsHandlers() {
    this.subscribedAppEvents.forEach((handlerObjects, eventName) => {
      handlerObjects.forEach((handlerObject: AppEventHandler) => {
        if (handlerObject.registeredHandler && handlerObject.isOn) {
          // @ts-expect-error off is defined in Evented
          this.off(eventName, handlerObject.registeredHandler);
          handlerObject.isOn = false;
        }
      });
    });

    this.subscribedAppEvents.clear();
    this.subscribedAppEvents = new Map();
  }

  willDestroy() {
    this.cleanupAppEventsHandlers();
    super.willDestroy();
  }
}
